// Analytics for the deeplink landing page — B2CAPPTH-2964 Section 3.
//
// The spec lists `deeplink_click` and `app_store_redirect` as Server-side, and
// `trackEvent` in api.js posts them to be-deeplink accordingly. That service is
// not deployed: /deeplink/v1/tracking/event answers 403 and /resolve answers
// 404, so both events have produced no data at all, while the six client-side
// events in the mobile app have been verified end to end. This module fills the
// top of the funnel from the browser instead, reporting into the SAME GA4
// property the app reports into, so the two halves join up. Both sides send
// `ref_id` under that name, which is what joins them.
//
// Uses gtag.js rather than the Firebase JS SDK, and that choice is the reason
// this can ship at all. The Firebase SDK needs an apiKey -- it resolves an app
// instance id through Firebase Installations, which throws
// `installations/missing-app-config-values` without one -- and that key trips
// secret scanning, which blocked the push and left the page silent through
// three deploys. gtag needs only the measurement id, which is not a credential
// and was never blocked. Events land in the same property either way, because
// the measurement id is what decides that. What is given up is the Firebase
// app-instance link, which matters for Remote Config and A/B testing on web;
// neither is used here.
//
// Two things to know when reading the numbers this produces:
//
//  - Browser-side counting is blockable. Ad blockers and tracking prevention
//    drop these requests, so this is a floor on real clicks, where the
//    server-side count would not have been. That is very likely why the spec
//    put these two on the server in the first place.
//
//  - When be-deeplink ships, `trackEvent` will start succeeding as well and the
//    same click will be counted twice. Whoever owns that rollout has to switch
//    one side off. Both sides are called from the one function in api.js so
//    there is a single place to do it.

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js?id=";

let gtagReady = null;

/**
 * The GA4 measurement id, read from REACT_APP_* like every other setting here.
 *
 * The only value this module needs. .env_prod stays blank until a Web data
 * stream exists in the production project — pointing production traffic at the
 * UAT stream would be worse than collecting nothing.
 */
const readMeasurementId = () => process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || null;

/**
 * `?debug_mode=1` on the landing URL routes these events to GA4 DebugView so
 * they can be watched live instead of waiting on the standard reports. Applied
 * at config time, which is where gtag wants it: passed alongside an event
 * instead it becomes an ordinary custom parameter and DebugView ignores it.
 */
const isDebugMode = () => {
  try {
    return new URLSearchParams(window.location.search).get("debug_mode") === "1";
  } catch (err) {
    return false;
  }
};

/**
 * Load gtag.js once and configure it.
 *
 * Injected rather than bundled, so the landing page does not pull a tag library
 * into the admin dashboard's build and a blocked or failed load degrades to "no
 * analytics" instead of a broken page. Resolves to the gtag function, or to
 * null when there is nothing to report to or the script could not load.
 */
const loadGtag = () => {
  if (gtagReady) return gtagReady;

  const measurementId = readMeasurementId();
  if (!measurementId) {
    gtagReady = Promise.resolve(null);
    return gtagReady;
  }

  gtagReady = new Promise(resolve => {
    try {
      window.dataLayer = window.dataLayer || [];
      // Pushes `arguments` itself rather than a rest array: gtag reads the raw
      // argument list off dataLayer, and spreading it into an array changes the
      // shape it expects.
      window.gtag =
        window.gtag ||
        function gtag() {
          // eslint-disable-next-line prefer-rest-params
          window.dataLayer.push(arguments);
        };

      const script = document.createElement("script");
      script.async = true;
      script.src = GTAG_SRC + encodeURIComponent(measurementId);
      script.onload = () => {
        window.gtag("js", new Date());
        window.gtag("config", measurementId, isDebugMode() ? { debug_mode: true } : {});
        resolve(window.gtag);
      };
      script.onerror = () => {
        // Blocked by an extension, or offline. Expected often enough that it is
        // not worth a console warning.
        resolve(null);
      };
      document.head.appendChild(script);
    } catch (err) {
      resolve(null);
    }
  });

  return gtagReady;
};

/**
 * Send one event.
 *
 * Never rejects and never blocks: a click has to reach the app whether or not
 * the measurement lands, which is the same rule `trackEvent` follows. Empty
 * values are dropped rather than sent as "undefined" strings.
 */
export const logLandingEvent = (eventName, params = {}) => {
  try {
    const payload = { platform_surface: "web_landing" };
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        payload[key] = value;
      }
    });

    return loadGtag()
      .then(gtag => {
        if (!gtag) return;
        gtag("event", eventName, payload);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn("[deeplink] analytics event failed:", eventName, err?.message);
      });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[deeplink] analytics event failed:", eventName, err?.message);
    return Promise.resolve();
  }
};

export default logLandingEvent;
